import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, Check, X, CurrencyDollar, Calendar,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function formatCurrency(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

const emptyForm = { amount: "", event_date: "", description: "" };

export default function CustomerRevenue({ customerId }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/revenue-events`);
      setEvents(data.events || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load revenue");
      setEvents([]); setTotal(0);
    } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const doAdd = async () => {
    if (form.amount === "" || form.amount === null) { toast.error("Amount is required"); return; }
    if (!form.event_date) { toast.error("Event date is required"); return; }
    setAdding(true);
    try {
      await api.post(`/customers/${customerId}/revenue-events`, {
        amount: Number(form.amount), event_date: form.event_date, description: form.description,
      });
      await load();
      setForm(emptyForm);
      toast.success("Revenue event added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally { setAdding(false); }
  };

  const startEdit = (e) => {
    setEditingId(e.revenue_event_id);
    setForm({ amount: String(e.amount ?? ""), event_date: e.event_date || "", description: e.description || "" });
  };

  const saveEdit = async (e) => {
    if (form.amount === "" || form.amount === null) { toast.error("Amount is required"); return; }
    if (!form.event_date) { toast.error("Event date is required"); return; }
    setBusyId(e.revenue_event_id);
    try {
      await api.patch(`/revenue-events/${e.revenue_event_id}`, {
        amount: Number(form.amount), event_date: form.event_date, description: form.description,
      });
      setEditingId(null); setForm(emptyForm);
      await load();
      toast.success("Updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusyId(null); }
  };

  const doDelete = async (e) => {
    if (!window.confirm(`Remove ${formatCurrency(e.amount)} entry from ${formatDate(e.event_date)}?`)) return;
    setBusyId(e.revenue_event_id);
    try {
      await api.delete(`/revenue-events/${e.revenue_event_id}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="revenue-panel">
      <div className="rounded-md border border-emerald-400/25 bg-emerald-400/5 p-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-300/80 flex items-center gap-1.5">
            <CurrencyDollar weight="duotone" className="h-3 w-3" /> Running total
          </div>
          <div data-testid="revenue-total" className="mt-1 font-display text-3xl text-emerald-200">
            {formatCurrency(total)}
          </div>
        </div>
        <div className="text-[11px] text-ink-secondary/80 max-w-xs">
          Sum of all revenue events for this customer. Single implicit currency — no FX conversion.
        </div>
      </div>

      {/* Add row */}
      {editingId === null && (
        <div className="rounded-md border border-white/10 bg-graphite-950/40 p-4">
          <div className="text-[11px] uppercase tracking-[0.24em] text-ink-secondary mb-3">Add revenue event</div>
          <div className="grid grid-cols-1 sm:grid-cols-[140px_160px_1fr_auto] gap-2 items-end">
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-[0.15em] text-ink-secondary">Amount</label>
              <Input data-testid="revenue-form-amount" type="number" min="0" step="0.01"
                value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                className="bg-graphite-900 border-white/10 text-sm" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-[0.15em] text-ink-secondary">Date</label>
              <Input data-testid="revenue-form-date" type="date"
                value={form.event_date} onChange={e => setForm({ ...form, event_date: e.target.value })}
                className="bg-graphite-900 border-white/10 text-sm" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-[0.15em] text-ink-secondary">Description (optional)</label>
              <Input data-testid="revenue-form-description" value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. January retainer"
                className="bg-graphite-900 border-white/10 text-sm" />
            </div>
            <button data-testid="revenue-add" onClick={doAdd}
              disabled={adding || form.amount === "" || !form.event_date}
              className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
              <Plus weight="bold" className="h-4 w-4" /> {adding ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      )}

      {/* Events list */}
      <div className="rounded-md border border-white/10 bg-graphite-950/40 overflow-hidden divide-y divide-white/5" data-testid="revenue-list">
        {loading ? (
          <div className="p-6 text-center text-sm text-ink-secondary">Loading revenue…</div>
        ) : events.length === 0 ? (
          <div className="p-6 text-center text-sm text-ink-secondary" data-testid="revenue-empty">
            No revenue events yet. Add the first one above.
          </div>
        ) : events.map((e) => {
          const editing = editingId === e.revenue_event_id;
          const busy = busyId === e.revenue_event_id;
          return (
            <div key={e.revenue_event_id} data-testid={`revenue-row-${e.revenue_event_id}`} className="p-4 flex items-start gap-3 flex-wrap">
              {editing ? (
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-[140px_160px_1fr_auto] gap-2 items-center">
                  <Input data-testid={`revenue-edit-amount-${e.revenue_event_id}`} type="number" min="0" step="0.01"
                    value={form.amount} onChange={ev => setForm({ ...form, amount: ev.target.value })}
                    className="bg-graphite-900 border-white/10 text-sm h-8" />
                  <Input data-testid={`revenue-edit-date-${e.revenue_event_id}`} type="date"
                    value={form.event_date} onChange={ev => setForm({ ...form, event_date: ev.target.value })}
                    className="bg-graphite-900 border-white/10 text-sm h-8" />
                  <Input data-testid={`revenue-edit-description-${e.revenue_event_id}`} value={form.description}
                    onChange={ev => setForm({ ...form, description: ev.target.value })}
                    className="bg-graphite-900 border-white/10 text-sm h-8" />
                  <div className="flex items-center gap-2">
                    <button data-testid={`revenue-save-${e.revenue_event_id}`} onClick={() => saveEdit(e)} disabled={busy}
                      className="rounded-full bg-white text-graphite-950 px-3 py-1 text-xs font-medium disabled:opacity-60">
                      <Check weight="bold" className="h-3 w-3 inline" /> Save
                    </button>
                    <button onClick={() => { setEditingId(null); setForm(emptyForm); }}
                      className="text-[11px] text-ink-secondary hover:text-ink-primary">
                      <X weight="bold" className="h-3 w-3 inline" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-32 font-display text-lg text-ink-primary" data-testid={`revenue-amount-${e.revenue_event_id}`}>
                    {formatCurrency(e.amount)}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="inline-flex items-center gap-1.5 text-xs text-ink-secondary">
                      <Calendar weight="duotone" className="h-3 w-3" />
                      <span data-testid={`revenue-date-${e.revenue_event_id}`}>{formatDate(e.event_date)}</span>
                    </div>
                    {e.description && (
                      <div className="mt-0.5 text-sm text-ink-secondary" data-testid={`revenue-description-${e.revenue_event_id}`}>{e.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`revenue-edit-${e.revenue_event_id}`} onClick={() => startEdit(e)}
                      disabled={busy}
                      className="p-1.5 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">
                      <PencilSimple weight="bold" className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`revenue-delete-${e.revenue_event_id}`} onClick={() => doDelete(e)}
                      disabled={busy}
                      className="p-1.5 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5 disabled:opacity-60">
                      <Trash weight="bold" className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
